import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {TranslocoService} from '@jsverse/transloco';

import {StatisticsResponse} from '../../models/statistics.model';
import {
  formatCount,
  formatDelta,
  StatisticsCategoryRow
} from '../../models/statistics-view.model';

/** Eine der vier Zahlen der Bilanzgleichung. */
interface BalanceItem {
  /** Rechenzeichen vor der Zahl, leer beim Anfangsbestand. */
  operator: string;
  label: string;
  value: number;
  /** Veraenderung zum Vorjahr, null wenn kein Vorjahr geladen ist. */
  delta: string | null;
  /** Erlaeuterung, u. a. das Zahlenpaar aus Ereignissen und Bilanzwirkung. */
  note: string;
  /** Die Ergebniszahl wird hervorgehoben. */
  isResult: boolean;
}

/** Ein Segment einer Bestandsaufteilung. */
interface SplitSegment {
  label: string;
  count: number;
  color: string;
  /** Anteil am Endbestand, 0-100. */
  percent: number;
}

interface StockSplit {
  title: string;
  /** Ausgeschriebene Summe, z. B. "348 + 1 + 18 + 0 = 367". */
  equation: string;
  segments: SplitSegment[];
}

export interface StatisticsTrendPoint {
  year: number;
  stock: number;
  isCurrentYear: boolean;
}

/** Farben der Aufenthaltsorte in der Reihenfolge, in der die API sie liefert. */
const LOCATION_COLORS = ['#7e9c30', '#c98317', '#3f5a12', '#a8b294'];
const STOCK_AGE_COLORS = ['#5d7d1f', '#b9cf8a'];

@Component({
  selector: 'app-statistics-overview',
  standalone: false,
  templateUrl: './statistics-overview.component.html',
  styleUrls: ['./statistics-overview.component.scss']
})
export class StatisticsOverviewComponent implements OnChanges {
  @Input({required: true}) response!: StatisticsResponse;
  @Input() previousResponse: StatisticsResponse | null = null;
  @Input() rows: StatisticsCategoryRow[] = [];
  @Input() trend: StatisticsTrendPoint[] = [];
  @Input() trendLoading = false;
  @Input() startLabel = '';
  @Input() endLabel = '';

  @Input() sortBySize = true;
  @Output() sortBySizeChange = new EventEmitter<boolean>();

  @Input() hideEmptyCategories = false;
  @Output() hideEmptyCategoriesChange = new EventEmitter<boolean>();

  @Input() compareWithPreviousYear = false;
  @Output() compareWithPreviousYearChange = new EventEmitter<boolean>();

  @Output() yearSelect = new EventEmitter<number>();

  balanceItems: BalanceItem[] = [];
  splits: StockSplit[] = [];
  balanceEquation = '';

  trendData: any = {labels: [], datasets: []};
  trendOptions: any = {};

  /** Stabile Referenz - ein Literal im Template wuerde p-selectbutton laufend neu aufbauen. */
  sortOptions: { label: string; value: boolean }[] = [];

  @ViewChild('dataQualitySection', {read: ElementRef}) dataQualitySection?: ElementRef<HTMLElement>;

  constructor(private readonly _translocoService: TranslocoService) {
    this.sortOptions = [
      {label: this._translocoService.translate('statistics.table.sortBySize'), value: true},
      {label: this._translocoService.translate('statistics.table.sortFixed'), value: false}
    ];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['response'] || changes['previousResponse'] || changes['startLabel'] || changes['endLabel']) {
      this.buildBalance();
      this.buildSplits();
    }
    if (changes['trend'] || changes['response']) {
      this.buildTrend();
    }
  }

  get hasPreviousYear(): boolean {
    return !!this.previousResponse;
  }

  get previousYearLabel(): string {
    return String(this.response.year - 1);
  }

  /** Vom Hinweis-Badge einer Tabellenzeile aus in den Arbeitsvorrat springen. */
  scrollToDataQuality(): void {
    this.dataQualitySection?.nativeElement.scrollIntoView({behavior: 'smooth', block: 'start'});
  }

  onTrendSelect(event: any): void {
    const index = event?.element?.index;
    const point = typeof index === 'number' ? this.trend[index] : undefined;
    if (point) {
      this.yearSelect.emit(point.year);
    }
  }

  /**
   * Die Bilanz als Rechnung: Anfangsbestand + Zugaenge - Abgaenge = Endbestand.
   * Die Hauptzahl ist immer die bilanzwirksame, die Ereigniszahl steht als
   * Erlaeuterung darunter.
   */
  private buildBalance(): void {
    const total = this.response.total;
    const previous = this.previousResponse?.total ?? null;
    const t = (key: string, params?: object) => this._translocoService.translate(`statistics.balance.${key}`, params);

    const delta = (value: number, previousValue: number | undefined): string | null => {
      if (previousValue === undefined) {
        return null;
      }
      return `${formatDelta(value - previousValue)} ${t('comparedTo', {year: this.response.year - 1})}`;
    };

    this.balanceItems = [
      {
        operator: '',
        label: `${this.startLabel}${this.response.year}`,
        value: total.stockAtPeriodStart,
        delta: delta(total.stockAtPeriodStart, previous?.stockAtPeriodStart),
        note: t('startNote', {year: this.response.year - 1}),
        isResult: false
      },
      {
        operator: '+',
        label: t('admissions'),
        value: total.admissions,
        delta: delta(total.admissions, previous?.admissions),
        note: total.returnsFromTrialOrFoster > 0
          ? t('admissionsNoteReturns', {
            events: formatCount(total.admissionEvents),
            returns: formatCount(total.returnsFromTrialOrFoster)
          })
          : t('admissionsNotePlain', {events: formatCount(total.admissionEvents)}),
        isResult: false
      },
      {
        operator: '−',
        label: t('departures'),
        value: total.departures,
        delta: delta(total.departures, previous?.departures),
        note: total.departureEvents > total.departures
          ? t('departuresNoteDiff', {
            events: formatCount(total.departureEvents),
            diff: formatCount(total.departureEvents - total.departures)
          })
          : t('departuresNotePlain', {events: formatCount(total.departureEvents)}),
        isResult: false
      },
      {
        operator: '=',
        label: `${this.endLabel}${this.response.year}`,
        value: total.stockAtPeriodEnd,
        delta: delta(total.stockAtPeriodEnd, previous?.stockAtPeriodEnd),
        note: this.response.isCurrentYear ? t('endNoteCurrent') : t('endNote', {year: this.response.year}),
        isResult: true
      }
    ];

    this.balanceEquation = `${formatCount(total.stockAtPeriodStart)} + ${formatCount(total.admissions)}`
      + ` − ${formatCount(total.departures)} = ${formatCount(total.stockAtPeriodEnd)}`;
  }

  /**
   * Zwei Sichten auf denselben Endbestand: nach Aufenthaltsort und nach
   * Zugangsjahr. Beide summieren sich auf den Endbestand - die Gleichung
   * steht ausgeschrieben daneben, damit das sichtbar ist.
   */
  private buildSplits(): void {
    const total = this.response.total;
    const stock = total.stockAtPeriodEnd;
    const breakdown = total.stockBreakdown;
    const t = (key: string, params?: object) => this._translocoService.translate(`statistics.split.${key}`, params);

    const toSegments = (entries: { label: string; count: number }[], colors: string[]): SplitSegment[] =>
      entries.map((entry, index) => ({
        label: entry.label,
        count: entry.count,
        color: colors[index % colors.length],
        percent: stock > 0 ? (entry.count / stock) * 100 : 0
      }));

    const equation = (entries: { count: number }[]): string =>
      `${entries.map(entry => formatCount(entry.count)).join(' + ')} = ${formatCount(stock)}`;

    const locationEntries = breakdown.byLocation.map(entry => ({label: entry.label, count: entry.count}));
    const ageEntries = [
      {label: t('fromPreviousYears'), count: breakdown.fromPreviousYears},
      {label: t('fromCurrentYear', {year: this.response.year}), count: breakdown.fromCurrentYear}
    ];

    this.splits = [
      {
        title: t('byLocation'),
        equation: equation(locationEntries),
        segments: toSegments(locationEntries, LOCATION_COLORS)
      },
      {
        title: t('byStockAge'),
        equation: equation(ageEntries),
        segments: toSegments(ageEntries, STOCK_AGE_COLORS)
      }
    ];
  }

  /** Endbestand je Jahr - das Einzige, was die Tabelle nicht zeigen kann. */
  private buildTrend(): void {
    const selectedYear = this.response.year;

    this.trendData = {
      labels: this.trend.map(point => point.isCurrentYear
        ? this._translocoService.translate('statistics.trend.currentYearLabel', {year: point.year})
        : String(point.year)),
      datasets: [{
        label: this._translocoService.translate('statistics.trend.datasetLabel'),
        data: this.trend.map(point => point.stock),
        backgroundColor: this.trend.map(point => point.year === selectedYear ? '#7e9c30' : '#d6e3c0'),
        borderRadius: 3,
        maxBarThickness: 44
      }]
    };

    this.trendOptions = {
      animation: false,
      responsive: true,
      maintainAspectRatio: false,
      onHover: (event: any) => {
        if (event?.native?.target) {
          event.native.target.style.cursor = 'pointer';
        }
      },
      plugins: {
        legend: {display: false},
        tooltip: {
          callbacks: {
            label: (context: any) => formatCount(context.parsed.y)
          }
        }
      },
      scales: {
        x: {grid: {display: false}},
        y: {beginAtZero: true, ticks: {precision: 0}}
      }
    };
  }
}
